import React, { useEffect, useState } from 'react';
import { useKanban } from '../context/KanbanContext';
import { Cliente, ESTADO_CLIENTE_LABELS } from '../types';
import { DocumentosClientePanel } from './DocumentosClientePanel';
import { ProyectosClientePanel } from './ProyectosClientePanel';
import { toast } from '../lib/toast';
import { useAuth } from '../context/AuthContext';

interface ClientesPanelProps {
    onClose: () => void;
}

interface ClienteFormData {
    nombre: string;
    empresa: string;
    color: string;
}

const formVacio: ClienteFormData = { nombre: '', empresa: '', color: '#64748b' };

export const ClientesPanel: React.FC<ClientesPanelProps> = ({ onClose }) => {
    const { clientesTodos, fetchClientesTodos, crearCliente, actualizarCliente, eliminarCliente } = useKanban();
    const { usuario } = useAuth();
    const puedeDocumentos = usuario?.rol === 'gerente' || Boolean(usuario?.permisos_modulos?.includes('documentos'));
    const [loading, setLoading] = useState(true);
    const [mensaje, setMensaje] = useState<{ tipo: 'ok' | 'error'; texto: string } | null>(null);

    const [mostrarNuevo, setMostrarNuevo] = useState(false);
    const [nuevoForm, setNuevoForm] = useState<ClienteFormData>(formVacio);
    const [creando, setCreando] = useState(false);

    const [editandoId, setEditandoId] = useState<number | null>(null);
    const [editForm, setEditForm] = useState<ClienteFormData>(formVacio);
    const [guardandoId, setGuardandoId] = useState<number | null>(null);
    const [eliminandoId, setEliminandoId] = useState<number | null>(null);
    const [documentosCliente, setDocumentosCliente] = useState<Cliente | null>(null);
    const [proyectosCliente, setProyectosCliente] = useState<Cliente | null>(null);

    useEffect(() => {
        (async () => {
            setLoading(true);
            await fetchClientesTodos();
            setLoading(false);
        })();
    }, [fetchClientesTodos]);

    const handleCrear = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!nuevoForm.nombre.trim() || !nuevoForm.empresa.trim()) return;

        setCreando(true);
        const success = await crearCliente({
            nombre: nuevoForm.nombre.trim(),
            empresa: nuevoForm.empresa.trim(),
            color: nuevoForm.color
        });
        setCreando(false);

        if (success) {
            setNuevoForm(formVacio);
            setMostrarNuevo(false);
            setMensaje({ tipo: 'ok', texto: 'Cliente creado correctamente.' });
        } else {
            setMensaje({ tipo: 'error', texto: 'No se pudo crear el cliente.' });
        }
    };

    const handleEditar = (cliente: Cliente) => {
        setEditandoId(cliente.id);
        setEditForm({ nombre: cliente.nombre, empresa: cliente.empresa, color: cliente.color });
        setMensaje(null);
    };

    const handleGuardarEdicion = async (id: number) => {
        if (!editForm.nombre.trim() || !editForm.empresa.trim()) return;

        setGuardandoId(id);
        const success = await actualizarCliente(id, {
            nombre: editForm.nombre.trim(),
            empresa: editForm.empresa.trim(),
            color: editForm.color
        });
        setGuardandoId(null);

        if (success) {
            setEditandoId(null);
            setMensaje({ tipo: 'ok', texto: 'Cliente actualizado correctamente.' });
        } else {
            setMensaje({ tipo: 'error', texto: 'No se pudo actualizar el cliente.' });
        }
    };

    const handleEliminar = async (cliente: Cliente) => {
        if (!await toast.confirm({
            title: `¿Eliminar al cliente "${cliente.empresa}"?`,
            description: 'Si tiene proyectos o tareas asociadas, en lugar de eliminarse se marcará como inactivo.',
            confirmText: 'Eliminar',
            type: 'warning'
        })) {
            return;
        }

        setEliminandoId(cliente.id);
        const resultado = await eliminarCliente(cliente.id);
        setEliminandoId(null);

        if (resultado.success) {
            setMensaje({
                tipo: 'ok',
                texto: resultado.resultado === 'definitivo'
                    ? 'Cliente eliminado correctamente.'
                    : 'El cliente tiene proyectos asociados: se marcó como inactivo.'
            });
        } else {
            setMensaje({ tipo: 'error', texto: resultado.message || 'No se pudo eliminar el cliente.' });
        }
    };

    const handleReactivar = async (cliente: Cliente) => {
        setGuardandoId(cliente.id);
        const success = await actualizarCliente(cliente.id, { estado: 'activo' });
        setGuardandoId(null);

        if (success) {
            setMensaje({ tipo: 'ok', texto: 'Cliente reactivado correctamente.' });
        } else {
            setMensaje({ tipo: 'error', texto: 'No se pudo reactivar el cliente.' });
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col transform transition-all"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-5 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center text-white text-lg">
                            👥
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-slate-800">Clientes</h2>
                            <p className="text-xs text-slate-500">Crea, edita y elimina los clientes del tablero</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="p-5 overflow-y-auto flex-1 space-y-3">
                    {mensaje && (
                        <div className={`text-sm rounded-xl px-4 py-2.5 border ${
                            mensaje.tipo === 'ok'
                                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                : 'bg-red-50 border-red-200 text-red-700'
                        }`}>
                            {mensaje.texto}
                        </div>
                    )}

                    {!mostrarNuevo ? (
                        <button
                            onClick={() => { setMostrarNuevo(true); setMensaje(null); }}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium border-2 border-dashed border-slate-200 text-slate-500 rounded-xl hover:border-blue-300 hover:text-blue-600 transition-colors"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Nuevo cliente
                        </button>
                    ) : (
                        <form onSubmit={handleCrear} className="bg-slate-50 rounded-xl p-4 space-y-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-slate-600 mb-1">Empresa *</label>
                                    <input
                                        type="text"
                                        value={nuevoForm.empresa}
                                        onChange={(e) => setNuevoForm(prev => ({ ...prev, empresa: e.target.value }))}
                                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-slate-600 mb-1">Contacto *</label>
                                    <input
                                        type="text"
                                        value={nuevoForm.nombre}
                                        onChange={(e) => setNuevoForm(prev => ({ ...prev, nombre: e.target.value }))}
                                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <label className="text-xs font-medium text-slate-600">Color</label>
                                <input
                                    type="color"
                                    value={nuevoForm.color}
                                    onChange={(e) => setNuevoForm(prev => ({ ...prev, color: e.target.value }))}
                                    className="w-10 h-8 rounded cursor-pointer border border-slate-200"
                                />
                            </div>
                            <div className="flex justify-end gap-2 pt-1">
                                <button
                                    type="button"
                                    onClick={() => { setMostrarNuevo(false); setNuevoForm(formVacio); }}
                                    className="px-3 py-2 text-xs font-medium border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={creando}
                                    className="px-3 py-2 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                                >
                                    {creando ? 'Creando...' : 'Crear cliente'}
                                </button>
                            </div>
                        </form>
                    )}

                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-32 gap-3">
                            <div className="relative">
                                <div className="w-10 h-10 border-4 border-slate-200 rounded-full"></div>
                                <div className="absolute inset-0 border-4 border-transparent border-t-blue-500 rounded-full animate-spin"></div>
                            </div>
                            <p className="text-slate-500 text-sm">Cargando clientes...</p>
                        </div>
                    ) : (
                        clientesTodos.map(cliente => (
                            <div
                                key={cliente.id}
                                className={`rounded-xl p-3 border ${
                                    cliente.estado === 'inactivo' ? 'bg-slate-50 border-slate-200 opacity-70' : 'bg-white border-slate-200'
                                }`}
                            >
                                {editandoId === cliente.id ? (
                                    <div className="space-y-3">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs font-medium text-slate-600 mb-1">Empresa</label>
                                                <input
                                                    type="text"
                                                    value={editForm.empresa}
                                                    onChange={(e) => setEditForm(prev => ({ ...prev, empresa: e.target.value }))}
                                                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-slate-600 mb-1">Contacto</label>
                                                <input
                                                    type="text"
                                                    value={editForm.nombre}
                                                    onChange={(e) => setEditForm(prev => ({ ...prev, nombre: e.target.value }))}
                                                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                />
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <label className="text-xs font-medium text-slate-600">Color</label>
                                                <input
                                                    type="color"
                                                    value={editForm.color}
                                                    onChange={(e) => setEditForm(prev => ({ ...prev, color: e.target.value }))}
                                                    className="w-10 h-8 rounded cursor-pointer border border-slate-200"
                                                />
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => setEditandoId(null)}
                                                    className="px-3 py-2 text-xs font-medium border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors"
                                                >
                                                    Cancelar
                                                </button>
                                                <button
                                                    onClick={() => handleGuardarEdicion(cliente.id)}
                                                    disabled={guardandoId === cliente.id}
                                                    className="px-3 py-2 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                                                >
                                                    {guardandoId === cliente.id ? 'Guardando...' : 'Guardar'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                        {/* Mismo rediseño que UsuariosPanel.tsx: separar "info" (punto+nombre,
                                            siempre en fila) de "acciones" (botones), apilados en columna en
                                            pantallas angostas y en fila desde sm — en vez de un único flex-wrap
                                            donde punto+nombre+4 botones competían por la misma línea y, según
                                            el ancho, terminaban o aplastando el nombre a unos pocos px o
                                            desbordando con scroll horizontal (probado empíricamente con ambas
                                            variantes antes de llegar a esta). */}
                                        <div className="flex items-center gap-3 min-w-0 flex-1">
                                            <div
                                                className="w-4 h-4 rounded-full flex-shrink-0"
                                                style={{ backgroundColor: cliente.color }}
                                            ></div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <p className="text-sm font-medium text-slate-700 truncate">{cliente.empresa}</p>
                                                    <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${
                                                        cliente.estado === 'activo'
                                                            ? 'bg-emerald-100 text-emerald-700'
                                                            : 'bg-slate-200 text-slate-500'
                                                    }`}>
                                                        {ESTADO_CLIENTE_LABELS[cliente.estado]}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-slate-500 truncate">{cliente.nombre}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap sm:flex-shrink-0">
                                            {cliente.estado === 'inactivo' && (
                                                <button
                                                    onClick={() => handleReactivar(cliente)}
                                                    disabled={guardandoId === cliente.id}
                                                    className="px-3 py-1.5 text-xs font-medium border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50"
                                                >
                                                    {guardandoId === cliente.id ? '...' : 'Reactivar'}
                                                </button>
                                            )}
                                            <button
                                                onClick={() => setProyectosCliente(cliente)}
                                                className="px-3 py-1.5 text-xs font-medium border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
                                            >
                                                📁 Proyectos
                                            </button>
                                            {puedeDocumentos && (
                                                <button
                                                    onClick={() => setDocumentosCliente(cliente)}
                                                    className="px-3 py-1.5 text-xs font-medium border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
                                                >
                                                    📄 Documentos
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handleEditar(cliente)}
                                                className="px-3 py-1.5 text-xs font-medium border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
                                            >
                                                Editar
                                            </button>
                                            <button
                                                onClick={() => handleEliminar(cliente)}
                                                disabled={eliminandoId === cliente.id}
                                                className="px-3 py-1.5 text-xs font-medium border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                                            >
                                                {eliminandoId === cliente.id ? '...' : 'Eliminar'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>

                <div className="flex items-center justify-end p-5 border-t border-slate-100 bg-slate-50/50 rounded-b-2xl">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-100 transition-colors font-medium"
                    >
                        Cerrar
                    </button>
                </div>
            </div>

            {documentosCliente && (
                <DocumentosClientePanel
                    cliente={documentosCliente}
                    onClose={() => setDocumentosCliente(null)}
                />
            )}

            {proyectosCliente && (
                <ProyectosClientePanel
                    cliente={proyectosCliente}
                    onClose={() => setProyectosCliente(null)}
                />
            )}
        </div>
    );
};
